import React, { useState, useRef } from "react";
import {
  Typography,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Box,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form, FieldArray } from "formik";
import Signature from "../../../../common/signature/signature";
import { Delete } from "@mui/icons-material";
import ReusableDialog from "../../../../common/dialog/dialog";
import PhoneInput from "react-phone-input-2";
import * as Yup from "yup"; // Import Yup for validation

// Define validation schema
const validationSchema = Yup.object().shape({
  personName: Yup.string().required("Person Name is required"),
  date: Yup.string().required("Date is required"),
  reasonForAssessment: Yup.string().required("Reason for assessment is required"),
  training: Yup.string().required("Training information is required"),
  risks: Yup.array().of(
    Yup.object().shape({
      risk: Yup.string().required("Risk is required"),
      likelihood: Yup.string().required("Likelihood is required"),
      impact: Yup.string().required("Impact is required"),
      mitigation: Yup.string().required("Mitigation is required"),
      contingency: Yup.string().required("Contingency is required"),
    })
  ),
  emergencyServices: Yup.object().shape({
    phone: Yup.string().required("Emergency services phone is required"),
    website: Yup.string().url("Must be a valid URL"),
    info: Yup.string(),
  }),
  edt: Yup.object().shape({
    phone: Yup.string().required("EDT phone is required"),
    website: Yup.string().url("Must be a valid URL"),
    info: Yup.string(),
  }),
  acolOnCallManager: Yup.object().shape({
    phone: Yup.string().required("ACOL on Call Manager phone is required"),
    website: Yup.string().url("Must be a valid URL"),
    info: Yup.string(),
  }),
  family: Yup.object().shape({
    phone: Yup.string().required("Family phone is required"),
    website: Yup.string().url("Must be a valid URL"),
    info: Yup.string(),
  }),
  expand: Yup.string().required("Training information is required"),
});

const RiskAssessmentAdmission = () => {
  const signatureRef = useRef(null);
  const [openAddItemDialog, setOpenAddItemDialog] = useState(false);
  const [newRisk, setNewRisk] = useState({
    risk: "",
    likelihood: "",
    impact: "",
    mitigation: "",
    contingency: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRisk((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLikelihoodChange = (value) => {
    setNewRisk((prev) => ({
      ...prev,
      likelihood: value,
    }));
  };

  const handleAdd = (push) => {
    push(newRisk);
    setOpenAddItemDialog(false);
    setNewRisk({ risk: "", likelihood: "", impact: "", mitigation: "", contingency: "" });
  };

  // Check if all fields are filled
  const isAddButtonDisabled = !(
    newRisk.risk &&
    newRisk.likelihood &&
    newRisk.impact &&
    newRisk.mitigation &&
    newRisk.contingency
  );

  return (
    <Formik
      initialValues={{
        personName: "",
        date: "",
        reasonForAssessment: "",
        training: "",
        risks: [],
        emergencyServices: { phone: "", website: "", info: "" },
        edt: { phone: "", website: "", info: "" },
        acolOnCallManager: { phone: "", website: "", info: "" },
        family: { phone: "", website: "", info: "" },
        expand: "",
      }}
      validationSchema={validationSchema} // Apply validation schema
      onSubmit={(values) => {
        const signatureData = signatureRef.current?.getSignatureData();
        if (!signatureData) {
          alert("Please provide a signature");
          return;
        }
        const formData = { ...values, signature: signatureData };
        console.log("Form data submitted:", formData);
      }}
    >
      {({ values, setFieldValue, touched, errors, handleBlur, handleChange }) => (
        <Form className='admission-form'>
          <Box mb={2}>
            <Chip label='Admission' />
            <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
              Risk Assessment
            </Typography>
          </Box>
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px" }} align='left'>
                Person Completing Risk Assessment
              </Typography>
              <TextField
                fullWidth
                name='personName'
                value={values.personName}
                onChange={handleChange}
                error={touched.personName && Boolean(errors.personName)}
                helperText={touched.personName && errors.personName}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px" }} align='left'>
                Date
              </Typography>
              <TextField
                fullWidth
                type='date'
                InputLabelProps={{ shrink: true }}
                name='date'
                value={values.date}
                onChange={handleChange}
                error={touched.date && Boolean(errors.date)}
                helperText={touched.date && errors.date}
              />
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
              Reason for assessment
            </Typography>
            <Typography sx={{ fontSize: "14px" }} align='left'>
              What training is required to ensure those named above can assist with the evacuation?
            </Typography>
            <TextField
              fullWidth
              name='reasonForAssessment'
              value={values.reasonForAssessment}
              onChange={(e) => setFieldValue("reasonForAssessment", e.target.value)}
              multiline
              rows={3}
              error={touched.reasonForAssessment && Boolean(errors.reasonForAssessment)}
              helperText={touched.reasonForAssessment && errors.reasonForAssessment}
            />
          </Box>

          <Box mt={4}>
            <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
              Add Risks That Could Happen
            </Typography>
            <FieldArray name='risks'>
              {({ remove, push }) => (
                <>
                  <TableContainer component={Paper}>
                    <Table aria-label='risk assessment table'>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Risk
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Rate
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Impact
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Mitigation
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Contingency
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.risks.map((risk, index) => {
                          const color =
                            risk.likelihood === "Low" ? "success" : risk.likelihood === "Medium" ? "warning" : "error";
                          return (
                            <TableRow key={index}>
                              <TableCell>{risk.risk}</TableCell>
                              <TableCell>
                                <Button color={color} variant='contained'>
                                  {risk.likelihood}
                                </Button>
                              </TableCell>
                              <TableCell>{risk.impact}</TableCell>
                              <TableCell>{risk.mitigation}</TableCell>
                              <TableCell>{risk.contingency}</TableCell>
                              <TableCell align='right'>
                                <IconButton color='primary' onClick={() => remove(index)}>
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button color='primary' startIcon={<AddIcon />} onClick={() => setOpenAddItemDialog(true)} mt={2}>
                    Add a Another Record
                  </Button>

                  <ReusableDialog
                    isOpen={openAddItemDialog}
                    onClose={() => setOpenAddItemDialog(false)}
                    title='Add a Risk?'
                    onSubmit={() => handleAdd(push)}
                    contentText={
                      "Add the relavent details to the fields to add a another perosn to the involved parties."
                    }
                    submitText='Proceed'
                    cancelText='Cancel'
                    disable={isAddButtonDisabled}
                  >
                    <Box mt={2}>
                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                        Risk
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder='Enter the Risk'
                        name='risk'
                        value={newRisk.risk}
                        onChange={handleInputChange}
                        variant='outlined'
                        margin='normal'
                      />

                      <Typography variant='subtitle1' mt={2}>
                        Likelihood
                      </Typography>
                      <Box display='flex' gap={1} mt={1} mb={2}>
                        <Button
                          variant={newRisk.likelihood === "Low" ? "contained" : "outlined"}
                          color='success'
                          onClick={() => handleLikelihoodChange("Low")}
                        >
                          Low
                        </Button>
                        <Button
                          variant={newRisk.likelihood === "Medium" ? "contained" : "outlined"}
                          color='warning'
                          onClick={() => handleLikelihoodChange("Medium")}
                        >
                          Medium
                        </Button>
                        <Button
                          variant={newRisk.likelihood === "High" ? "contained" : "outlined"}
                          color='error'
                          onClick={() => handleLikelihoodChange("High")}
                        >
                          High
                        </Button>
                      </Box>

                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                        Impact
                      </Typography>
                      <TextField
                        fullWidth
                        name='impact'
                        value={newRisk.impact}
                        onChange={handleInputChange}
                        variant='outlined'
                        margin='normal'
                        multiline
                        rows={3}
                      />
                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                        Mitigation
                      </Typography>
                      <TextField
                        fullWidth
                        name='mitigation'
                        value={newRisk.mitigation}
                        onChange={handleInputChange}
                        variant='outlined'
                        margin='normal'
                        multiline
                        rows={3}
                      />
                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                        Contingency
                      </Typography>
                      <TextField
                        fullWidth
                        name='contingency'
                        value={newRisk.contingency}
                        onChange={handleInputChange}
                        variant='outlined'
                        margin='normal'
                        multiline
                        rows={3}
                      />
                    </Box>
                  </ReusableDialog>
                </>
              )}
            </FieldArray>
          </Box>

          <Box mt={4}>
            <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
              Training
            </Typography>
            <Typography sx={{ fontSize: "14px" }} align='left'>
              Identify the level and extent of information, instruction and training required. Consider experience of
              young person, their care program and risk assessment
            </Typography>
            <TextField
              fullWidth
              name='training'
              value={values.training}
              onChange={handleChange}
              multiline
              rows={3}
              error={touched.training && Boolean(errors.training)}
              helperText={touched.training && errors.training}
            />
          </Box>

          <Box mt={2}>
            <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
              Expand and clarify, if necessary
            </Typography>
            <TextField
              fullWidth
              name='expand'
              value={values.expand}
              onChange={handleChange}
              multiline
              rows={3}
              error={touched.expand && Boolean(errors.expand)}
              helperText={touched.expand && errors.expand}
            />
          </Box>

          <Box mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
              Emergency Services
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Mobile Phone no
                </Typography>
                <PhoneInput
                  country='gb'
                  value={values.emergencyServices.phone}
                  onChange={(phone) => setFieldValue("emergencyServices.phone", phone)}
                  onBlur={handleBlur}
                  inputProps={{
                    name: "emergencyServices.phone",
                    required: true,
                  }}
                  containerStyle={{ width: "100%" }}
                  inputStyle={{
                    width: "100%",
                    height: "55px",
                    fontSize: "16px",
                  }}
                />
                {touched.emergencyServices?.phone && errors.emergencyServices?.phone && (
                  <Typography color='error' variant='caption'>
                    {errors.emergencyServices.phone}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={5}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Website
                </Typography>
                <TextField
                  fullWidth
                  placeholder='Enter website'
                  name='emergencyServices.website'
                  value={values.emergencyServices.website}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.emergencyServices?.website && Boolean(errors.emergencyServices?.website)}
                  helperText={touched.emergencyServices?.website && errors.emergencyServices?.website}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={10}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Additional Information
                </Typography>
                <TextField
                  fullWidth
                  name='emergencyServices.info'
                  value={values.emergencyServices.info}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.emergencyServices?.info && Boolean(errors.emergencyServices?.info)}
                  helperText={touched.emergencyServices?.info && errors.emergencyServices?.info}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
              EDT
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Mobile Phone no
                </Typography>
                <PhoneInput
                  country='gb'
                  value={values.edt.phone}
                  onChange={(phone) => setFieldValue("edt.phone", phone)}
                  onBlur={handleBlur}
                  inputProps={{
                    name: "edt.phone",
                    required: true,
                  }}
                  containerStyle={{ width: "100%" }}
                  inputStyle={{
                    width: "100%",
                    height: "55px",
                    fontSize: "16px",
                  }}
                />
                {touched.edt?.phone && errors.edt?.phone && (
                  <Typography color='error' variant='caption'>
                    {errors.edt.phone}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={5}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Website
                </Typography>
                <TextField
                  fullWidth
                  placeholder='Enter website'
                  name='edt.website'
                  value={values.edt.website}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.edt?.website && Boolean(errors.edt?.website)}
                  helperText={touched.edt?.website && errors.edt?.website}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={10}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Additional Information
                </Typography>
                <TextField
                  fullWidth
                  name='edt.info'
                  value={values.edt.info}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.edt?.info && Boolean(errors.edt?.info)}
                  helperText={touched.edt?.info && errors.edt?.info}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
              ACOL on Call Manager
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Mobile Phone no
                </Typography>
                <PhoneInput
                  country='gb'
                  value={values.acolOnCallManager.phone}
                  onChange={(phone) => setFieldValue("acolOnCallManager.phone", phone)}
                  onBlur={handleBlur}
                  inputProps={{
                    name: "acolOnCallManager.phone",
                    required: true,
                  }}
                  containerStyle={{ width: "100%" }}
                  inputStyle={{
                    width: "100%",
                    height: "55px",
                    fontSize: "16px",
                  }}
                />
                {touched.acolOnCallManager?.phone && errors.acolOnCallManager?.phone && (
                  <Typography color='error' variant='caption'>
                    {errors.acolOnCallManager.phone}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={5}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Website
                </Typography>
                <TextField
                  fullWidth
                  placeholder='Enter website'
                  name='acolOnCallManager.website'
                  value={values.acolOnCallManager.website}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.acolOnCallManager?.website && Boolean(errors.acolOnCallManager?.website)}
                  helperText={touched.acolOnCallManager?.website && errors.acolOnCallManager?.website}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={10}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Additional Information
                </Typography>
                <TextField
                  fullWidth
                  name='acolOnCallManager.info'
                  value={values.acolOnCallManager.info}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.acolOnCallManager?.info && Boolean(errors.acolOnCallManager?.info)}
                  helperText={touched.acolOnCallManager?.info && errors.acolOnCallManager?.info}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
              Family
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Mobile Phone no
                </Typography>
                <PhoneInput
                  country='gb'
                  value={values.family.phone}
                  onChange={(phone) => setFieldValue("family.phone", phone)}
                  onBlur={handleBlur}
                  inputProps={{
                    name: "family.phone",
                    required: true,
                  }}
                  containerStyle={{ width: "100%" }}
                  inputStyle={{
                    width: "100%",
                    height: "55px",
                    fontSize: "16px",
                  }}
                />
                {touched.family?.phone && errors.family?.phone && (
                  <Typography color='error' variant='caption'>
                    {errors.family.phone}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={5}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Website
                </Typography>
                <TextField
                  fullWidth
                  placeholder='Enter website'
                  name='family.website'
                  value={values.family.website}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.family?.website && Boolean(errors.family?.website)}
                  helperText={touched.family?.website && errors.family?.website}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={10}>
                <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                  Additional Information
                </Typography>
                <TextField
                  fullWidth
                  name='family.info'
                  value={values.family.info}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.family?.info && Boolean(errors.family?.info)}
                  helperText={touched.family?.info && errors.family?.info}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Signature */}
          <Box className='signature-box'>
            <Signature ref={signatureRef} />
            <Typography align='right'>
              <Grid container>
                <Grid>
                  <Typography sx={{ fontSize: "14px", color: "#FFB700" }}>Venorox </Typography>
                </Grid>
                <Grid>
                  <Grid>
                    <Typography sx={{ fontSize: "14px", color: "black" }}>/ Signature</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Typography sx={{ fontSize: "13px"}}>2024/03/12</Typography>
              <Grid className='clear-signature-button'>
                <Button variant='outlined' onClick={() => signatureRef.current?.clearSignature()}>
                  Clear
                </Button>
              </Grid>
            </Typography>
          </Box>

          {/* Submit button */}
          <Grid item xs={12} mt={2}>
            <div className='submit-button-container'>
              <Button type='submit' variant='contained' color='primary' className='submit-button'>
                Submit
              </Button>
            </div>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default RiskAssessmentAdmission;
